import { Component, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import {
  ProtocolTypeDetailDto,
  ProtocolTypeMergeDto,
  ProtocolTypeService,
} from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { PendingChanges } from '@guards/pending-changes.guard';

@Component({
  standalone: false,
  selector: 'wr-protocol-type-settings',
  templateUrl: './protocol-type-settings.component.html',
  styleUrls: ['./protocol-type-settings.component.scss'],
})
export class ProtocolTypeSettingsComponent implements PendingChanges {

  readonly saving$ = new BehaviorSubject(false);
  readonly isEditing = signal(false);
  readonly canEdit = signal(false);

  protocolTypeId: string | null = null;

  form = this.buildForm(null);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly protocolTypeService: ProtocolTypeService,
    private readonly toastrService: NbToastrService,
  ) {
    this.route.paramMap.subscribe(params => {
      const id = this.protocolTypeId = params.get('id');
      if (id && id !== 'new') {
        this.protocolTypeService.findById(id).subscribe(dto => {
          this.canEdit.set(dto.permissions.canEdit);
          this.form = this.buildForm(dto);
          if (!this.isEditing()) {
            this.form.disable();
          }
        });
      }
    });
  }

  buildForm(dto: ProtocolTypeDetailDto | null) {
    return createForm(({ field }) => ({
      addAdditionalCollections: field({ value: dto?.settings?.addAdditionalCollections ?? false }),
    }));
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.form.enable();
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    if (this.protocolTypeId) {
      const dto = await firstValueFrom(this.protocolTypeService.findById(this.protocolTypeId));
      this.form = this.buildForm(dto);
      this.form.disable();
    }
  }

  async submit(): Promise<boolean> {
    if (!this.protocolTypeId) return false;
    try {
      this.saving$.next(true);
      const mergeDto: ProtocolTypeMergeDto = {
        id: this.protocolTypeId,
        settings: { addAdditionalCollections: this.form.value.addAdditionalCollections },
      };
      await firstValueFrom(this.protocolTypeService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('Einstellungen gespeichert', 'Erfolg');
      const dto = await firstValueFrom(this.protocolTypeService.findById(this.protocolTypeId));
      this.form = this.buildForm(dto);
      this.form.disable();
      return true;
    } catch {
      this.toastrService.danger('Einstellungen konnten nicht gespeichert werden.', 'Fehler');
      return false;
    } finally {
      this.saving$.next(false);
    }
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }
}
